import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { SalesOrderResultDTO, SalesOrderStatus } from '@app/api/purchase-service-sales';
import { PermissionService } from '@app/core/services/permission.service';
import {
  DISTRIBUTION_PAGE_SIZE,
  SALES_ORDER_STATUSES,
  salesOrderSeverity,
} from '../distribution.constants';
import { SalesOrderService } from './services/sales-order.service';

@Component({
  selector: 'app-sales-order',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ButtonModule,
    ConfirmDialogModule,
    InputTextModule,
    SelectModule,
    TableModule,
    TagModule,
    ToastModule,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './sales-order.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SalesOrderComponent {
  private readonly router = inject(Router);
  private readonly service = inject(SalesOrderService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly translate = inject(TranslateService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    { initialValue: this.permissionService.getCurrentPermissions() },
  );

  readonly canCreate = computed(() => this.can('create'));
  readonly canApprove = computed(() => this.can('approve'));
  readonly canRelease = computed(() => this.can('release'));

  readonly statusSeverity = salesOrderSeverity;
  readonly statusOptions = SALES_ORDER_STATUSES.map((value) => ({
    value,
    label: `distribution.status.order.${value}`,
  }));

  readonly rows = signal<SalesOrderResultDTO[]>([]);
  readonly total = signal(0);
  readonly loading = signal(false);
  readonly first = signal(0);
  readonly pageSize = DISTRIBUTION_PAGE_SIZE;

  series = '';
  vendorCode = '';
  status: SalesOrderStatus | null = null;

  private can(action: string): boolean {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      `distribution.sales_order.${action}`,
    );
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    this.first.set(event.first ?? 0);
    this.load(event.rows ?? this.pageSize);
  }

  onSearch(): void {
    this.first.set(0);
    this.load(this.pageSize);
  }

  onReset(): void {
    this.series = '';
    this.vendorCode = '';
    this.status = null;
    this.onSearch();
  }

  onAdd(): void {
    this.router.navigate(['/distribution/sales-order/add']);
  }

  onView(row: SalesOrderResultDTO): void {
    this.router.navigate(['/distribution/sales-order/view', row.id]);
  }

  onApprove(row: SalesOrderResultDTO): void {
    this.confirmAndRun(
      'distribution.sales_order.confirm_approve',
      row,
      () => this.service.approve(row.id!),
    );
  }

  onRelease(row: SalesOrderResultDTO): void {
    this.confirmAndRun(
      'distribution.sales_order.confirm_release',
      row,
      () => this.service.release(row.id!),
    );
  }

  private confirmAndRun(
    messageKey: string,
    row: SalesOrderResultDTO,
    action: () => ReturnType<SalesOrderService['approve']>,
  ): void {
    this.confirmationService.confirm({
      message: this.translate.instant(messageKey, { series: row.series }),
      accept: () =>
        action().subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: this.translate.instant('common.messages.success'),
              detail: this.translate.instant('distribution.messages.updated'),
            });
            this.load(this.pageSize);
          },
          error: (err) => this.showError(err),
        }),
    });
  }

  private load(size: number): void {
    this.loading.set(true);
    this.service
      .search({
        series: this.series,
        vendorCode: this.vendorCode,
        status: this.status,
        first: this.first(),
        rows: size,
      })
      .subscribe({
        next: (page) => {
          this.rows.set(page.pageContent ?? []);
          this.total.set(page.totalElements ?? 0);
          this.loading.set(false);
        },
        error: (err) => {
          this.loading.set(false);
          this.showError(err);
        },
      });
  }

  private showError(err: unknown): void {
    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('common.messages.error'),
      detail:
        (err as { error?: { message?: string } })?.error?.message ??
        this.translate.instant('distribution.messages.failed'),
    });
  }
}
